import { useCallback, useContext, useEffect, useMemo } from 'react'
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { NavContext } from '../context/global_context'
import { isAuth } from '../utils/auth'
import { getRequest } from '../services/base'
import 'react-toastify/dist/ReactToastify.css'
import { deleteLocalStorage } from '../utils/LocalStorageAccess'
import { AUTH_TOKEN, MAKE_ID } from '../constants/common'
import DtcsHeader from './dtcs/TATMappings/DtcsHeader'
import { deleteSessionStorage } from '../utils/SessionStorage'
import { USAGE_REPORT } from '../constants/permission'
import { Content, Header } from 'antd/es/layout/layout'
import { Button, Flex, Form, Layout, message, Typography } from 'antd'
import Logo from '../antd/Logo'
import { useThemeContext } from '../context/theme_context'
import { DARK_MODE, LIGHT_MODE } from '../constants'
import {
  HomeOutlined,
  InfoCircleOutlined,
  LoginOutlined,
  MoonOutlined,
  SunOutlined,
} from '@ant-design/icons'
import UserMenu from '../antd/user_menu/UserMenu'
import SelectInput from '../antd/common/SelectInput'
import MenuHistoryBackButton from '../antd/common/MenuHistoryBackButton'
import { ProgresSpinner } from '../antd/common/ProgresSpinner'

const AppLayout = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [value, dispatch] = useContext(NavContext)
  const [searchParams] = useSearchParams()
  const { mode, setMode } = useThemeContext()
  const [messageApi, toastContext] = message.useMessage()

  useEffect(() => {
    if (!isAuth) {
      if (location.pathname !== '/logout_success') {
        getCognitoRedirect()
      }
    } else {
      checkUserAuth()
    }
  }, [])

  useEffect(() => {
    if (value?.notification?.notification_type) {
      notify(value?.notification?.msg, value?.notification?.notification_type)
    } else if (value?.notification instanceof Array) {
      value?.notification.forEach((notification_row) => {
        notify(notification_row?.msg, notification_row?.notification_type)
      })
    }
  }, [value])

  const checkUserAuth = async () => {
    let res = await getRequest(`/api/ping/`)
    if (res?.msg === 'ok') {
      dispatch({
        type: 'permissions',
        data: res?.permissions,
        make: res?.make,
        username: res?.username,
        client_id: res?.client_id,
      })
    }
    if (res?.msg === 'ok' && location.pathname === '/') {
      window.location.href = '/vehicles'
    } else if (res?.msg === 'Not authorized') {
      callLogoutApi()
    }
  }

  const getCognitoRedirect = async () => {
    dispatch({
      type: 'loader',
      data: {
        caption: 'Wait!, We are validating login..',
        showLoader: true,
      },
      notification: {},
    })
    let res = await getRequest(`/generate_auth_redirect_url/`, false)

    dispatch({
      type: 'notification',
      data: {},
    })
    if (res?.redirect_url && location.pathname !== '/auth') {
      window.location.href = res?.redirect_url
    } else if (
      res?.msg ===
        'Oops! Something went wrong, You are not authorised to access' &&
      location.pathname !== '/not_permitted'
    ) {
      window.location.href = '/not_permitted'
    } else {
      if (
        ![
          '/users/sign_in',
          '/auth',
          '/not_permitted',
          '/logout_success',
        ].includes(location.pathname)
      ) {
        window.location.href = '/users/sign_in'
      }
    }
  }

  const Logout = (e) => {
    e.preventDefault()
    callLogoutApi()
  }

  const callLogoutApi = async () => {
    let res = await getRequest(`/logout/`)
    if (res.msg) {
      deleteLocalStorage(AUTH_TOKEN)
      deleteLocalStorage(MAKE_ID)
      deleteSessionStorage('vehicle')
      deleteSessionStorage('dtcs_list')
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: 'Logout Sucessfully done! We are redirecting you.',
        },
      })
      setTimeout(function () {
        window.location.href = '/logout_success'
      }, 1000)
    }
  }

  const notify = (msg, notification_type) => {
    messageApi.open({
      type: notification_type,
      content: msg,
      className: 'notification-toast',
      // duration: 40,
    })
  }

  const toggleTheme = useCallback(() => {
    const newTheme = mode == LIGHT_MODE ? DARK_MODE : LIGHT_MODE
    setMode(newTheme)
  }, [mode, setMode])

  const navigationOptions = useMemo(() => [
    {
      value: 'administration',
      label: 'Administration',
    },
    ...(value?.permissions?.includes(USAGE_REPORT)
      ? [
          {
            value: 'system_and_sub_system',
            label: 'System & Sub System',
          },
        ]
      : []),
    {
      value: 'usage_report',
      label: 'Usage Report',
    },
    {
      value: 'customer_setup',
      label: 'Customer Setup',
    },
  ], [value?.permissions])
  
  const currRoute = useMemo(() => location.pathname.replace('/', '').split('/')[0], [location.pathname])
  const routeValue = useMemo(() => navigationOptions.find(
    (option) => currRoute === option.value
  ), [navigationOptions, currRoute])

  const HeaderSection = () => {
    return (
      <Header className={`root-header-container`}>
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={16} className="left-container">
            <div className="header-image-container">
              <Logo navigateToDashboard={() => navigate('/vehicles')} />
            </div>
            {value?.show_back_btn && isAuth && (
              <MenuHistoryBackButton
                onBack={() => {
                  value?.back_btn_url
                    ? navigate(value?.back_btn_url)
                    : navigate(-1)
                }}
              />
            )}
            {value?.show_home_button && (
              <Button
                icon={<HomeOutlined />}
                onClick={() => {
                  navigate('/')
                }}
              >
                Home
              </Button>
            )}
            <Flex vertical={true} gap={10} className="heading-container">
              {value?.header && (
                <Typography.Title level={1} className="heading">
                  {value?.header}
                </Typography.Title>
              )}
              {value?.sub_header && (
                <Typography.Title level={2} className="breadcrumb">
                  {value?.sub_header}
                </Typography.Title>
              )}
            </Flex>

            {/* {value?.dtc_details?.length > 0 && (
              <div className="header-dtc-info">
                <div
                  className="py-2"
                  style={{
                    borderLeftWidth: '1px',
                    borderColor: 'white',
                  }}
                >
                  &nbsp;
                </div>
                <DtcsHeader
                  dtcs={value?.dtc_details}
                  vehicleSid={value?.vehicle}
                />
              </div>
            )} */}
          </Flex>

          <Flex align="center" gap={16} className="right-container">
            {isAuth ? (
              <>
                <UserMenu
                  userIsSignedIn={isAuth}
                  username={value?.username}
                  clientId={value?.client_id}
                  onLogout={Logout}
                  className="user-menu"
                />

                <Form
                  id="navigation"
                  name="navigation"
                  initialValues={{ nav_menu: routeValue?.value }}
                >
                  <SelectInput
                    name="nav_menu"
                    label="Select Section"
                    placeholder="Select"
                    options={navigationOptions}
                    wrapperClass="nav-menu"
                    hideLabel={true}
                    isRequired={false}
                    showSearch={false}
                    allowClear={false}
                    onChange={(option) => {
                      const { value, label } = option
                      navigate(`/${value}`)
                    }}
                  />
                </Form>

                <div className="header-theme-selector">
                  <Button
                    icon={
                      mode == DARK_MODE ? <MoonOutlined /> : <SunOutlined />
                    }
                    onClick={toggleTheme}
                  />
                </div>
              </>
            ) : (
              <Button
                icon={<LoginOutlined />}
                onClick={() => {
                  navigate('/users/sign_in')
                }}
              >
                Sign In
              </Button>
            )}
          </Flex>
        </Flex>
      </Header>
    )
  }

  return (
    <>
      {toastContext}
      <Layout className="root-layout">
        {/* Loader */}
        {value?.loader?.showLoader && (
          <div className="loading-overlay">
            <ProgresSpinner
              caption={
                value?.loader?.caption ? value?.loader?.caption : 'Loading...'
              }
              size="large"
            />
          </div>
        )}
        {/* Header */}
        {!searchParams.get('hide-nav') && <HeaderSection />}
        {/* Main Content */}
        <Content
          className={`root-content-container ${
            value?.full_width_container ? '' : 'container'
          }`}
          data-content="main"
        >
          <Outlet />
        </Content>
      </Layout>
    </>
  )
}

export default AppLayout
